import React from "react";
import {
  Button,
  Grid,
  TextField,
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  Checkbox
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { FilterList as FilterIcon } from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import useQueues from "../../hooks/useQueues";

const useStyles = makeStyles((theme) => ({
  filterCard: {
    background: theme.palette.background.paper,
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  
  filterHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  
  filterButton: {
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    minHeight: 48,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.75, 2),
      minHeight: 44,
      fontSize: '0.875rem',
    },
  },
  
  dateField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(1),
    },
  },
}));

const Filters = ({
  setDateStartTicket,
  setDateEndTicket,
  dateStartTicket,
  dateEndTicket,
  setQueueTicket,
  queueTicket,
  setUserTicket,
  userTicket,
  fetchData,
  attendants = [],
  comparisonEnabled = false,
  setComparisonEnabled,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const { findAll: findAllQueues } = useQueues();

  const [queues, setQueues] = React.useState([]);
  const [selectedQueue, setSelectedQueue] = React.useState(queueTicket || '');
  const [selectedUser, setSelectedUser] = React.useState(userTicket || '');
  const [dateStart, setDateStart] = React.useState(dateStartTicket);
  const [dateEnd, setDateEnd] = React.useState(dateEndTicket);
  const [loading, setLoading] = React.useState(false);

  // Carregar filas ao montar o componente
  React.useEffect(() => {
    const loadQueues = async () => {
      try {
        const queuesList = await findAllQueues();
        setQueues(queuesList || []);
      } catch (error) {
        console.error('Erro ao carregar filas:', error);
      }
    };
    loadQueues();
  }, [findAllQueues]);

  const handleApplyFilters = async () => {
    setLoading(true);
    try {
      setQueueTicket(selectedQueue);
      setUserTicket(selectedUser);
      setDateStartTicket(dateStart);
      setDateEndTicket(dateEnd);
      // Toggle parent's fetchDataFilter to trigger data refetch
      fetchData(prev => !prev);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSelectedQueue('');
    setSelectedUser('');
    setQueueTicket('');
    setUserTicket('');
  };

  return (
    <Card className={classes.filterCard}>
      <CardContent>
        <Box className={classes.filterHeader}>
          <FilterIcon />
          <Typography variant="h6" component="h2">
            {i18n.t("dashboard.filters")}
          </Typography>
        </Box>

        <Grid container spacing={isMdUp ? 3 : 2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              name="dateStart"
              label={i18n.t("dashboard.date.initialDate")}
              InputLabelProps={{
                shrink: true,
              }}
              type="date"
              variant="outlined"
              size={isSmUp ? "medium" : "small"}
              defaultValue={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              className={classes.dateField}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              name="dateEnd"
              label={i18n.t("dashboard.date.finalDate")}
              InputLabelProps={{
                shrink: true,
              }}
              type="date"
              variant="outlined"
              size={isSmUp ? "medium" : "small"}
              defaultValue={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              className={classes.dateField}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl
              fullWidth
              variant="outlined"
              size={isSmUp ? "medium" : "small"}
              className={classes.dateField}
            >
              <InputLabel>{i18n.t("dashboard.filters.queue")}</InputLabel>
              <Select
                value={selectedQueue}
                onChange={(e) => setSelectedQueue(e.target.value)}
                label={i18n.t("dashboard.filters.queue")}
              >
                <MenuItem value="">
                  <em>{i18n.t("dashboard.filters.allQueues")}</em>
                </MenuItem>
                {queues.map((queue) => (
                  <MenuItem key={queue.id} value={queue.id}>
                    {queue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl
              fullWidth
              variant="outlined"
              size={isSmUp ? "medium" : "small"}
              className={classes.dateField}
            >
              <InputLabel>{i18n.t("dashboard.filters.attendant")}</InputLabel>
              <Select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                label={i18n.t("dashboard.filters.attendant")}
              >
                <MenuItem value="">
                  <em>{i18n.t("dashboard.filters.allAttendants")}</em>
                </MenuItem>
                {attendants.map((attendant) => (
                  <MenuItem key={attendant.id} value={attendant.id}>
                    {attendant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleApplyFilters}
              disabled={loading}
              className={classes.filterButton}
              size={isSmUp ? "large" : "medium"}
            >
              {loading ? 'Aplicando...' : i18n.t("dashboard.filter")}
            </Button>
          </Grid>

          {setComparisonEnabled && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={comparisonEnabled}
                    onChange={(e) => setComparisonEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label={i18n.t("dashboard.comparison.enableComparison")}
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

Filters.displayName = 'Filters';

export default Filters;
